/**
 * RedDotDispositionOptions — shared radio-card list for choosing what happens to a client's
 * open Red Dots when their Overseeing BCBA changes. Used by SetOverseeingBcbaModal (single
 * client) and CaseloadTransferModal (bulk wizard).
 *
 * Radio-card styling lifted from CaseloadTransferConflictModal.tsx (do not modify that file —
 * it stays unwired to this component for now).
 */

import styled from 'styled-components';
import { media } from '../../styles/media';
import type { RedDotDisposition } from '../../services/api/clientService';

interface RedDotDispositionOptionsProps {
  value: RedDotDisposition;
  onChange: (value: RedDotDisposition) => void;
  /** Number of open Red Dots affected — drives the copy on each card. */
  openRedDotCount: number;
  /** Name of the incoming BCBA, used in the TRANSFER_TO_NEW card. */
  newOwnerName: string;
  /** Name of the outgoing BCBA, used in the KEEP_WITH_OUTGOING card. */
  outgoingOwnerName: string;
}

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const RadioLabel = styled.label<{ $selected: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]};
  border: 1.5px solid
    ${({ theme, $selected }) =>
      $selected ? theme.colors.primary.base : theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme, $selected }) =>
    $selected
      ? (theme.colors.primary.lightSky ?? theme.colors.backgrounds.light)
      : theme.colors.backgrounds.white};
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.base};
  }

  ${media.tablet} {
    padding: ${({ theme }) => theme.spacing[2]};
  }
`;

const RadioInput = styled.input`
  margin-top: 2px;
  flex-shrink: 0;
  accent-color: ${({ theme }) => theme.colors.primary.base};
`;

const RadioContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;

const RadioTitle = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RadioDesc = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.4;
  overflow-wrap: anywhere;
`;

const dotWord = (count: number) => `${count} open Red Dot${count === 1 ? '' : 's'}`;

export function RedDotDispositionOptions({
  value,
  onChange,
  openRedDotCount,
  newOwnerName,
  outgoingOwnerName,
}: RedDotDispositionOptionsProps) {
  return (
    <RadioGroup role="radiogroup" aria-label="Red Dot disposition">
      <RadioLabel $selected={value === 'TRANSFER_TO_NEW'}>
        <RadioInput
          type="radio"
          name="redDotDisposition"
          value="TRANSFER_TO_NEW"
          checked={value === 'TRANSFER_TO_NEW'}
          onChange={() => onChange('TRANSFER_TO_NEW')}
        />
        <RadioContent>
          <RadioTitle>Transfer to new BCBA</RadioTitle>
          <RadioDesc>
            {dotWord(openRedDotCount)} move to {newOwnerName}.
          </RadioDesc>
        </RadioContent>
      </RadioLabel>

      <RadioLabel $selected={value === 'KEEP_WITH_OUTGOING'}>
        <RadioInput
          type="radio"
          name="redDotDisposition"
          value="KEEP_WITH_OUTGOING"
          checked={value === 'KEEP_WITH_OUTGOING'}
          onChange={() => onChange('KEEP_WITH_OUTGOING')}
        />
        <RadioContent>
          <RadioTitle>Keep with outgoing BCBA</RadioTitle>
          <RadioDesc>
            {dotWord(openRedDotCount)} stay with {outgoingOwnerName}.
          </RadioDesc>
        </RadioContent>
      </RadioLabel>

      <RadioLabel $selected={value === 'UNASSIGN'}>
        <RadioInput
          type="radio"
          name="redDotDisposition"
          value="UNASSIGN"
          checked={value === 'UNASSIGN'}
          onChange={() => onChange('UNASSIGN')}
        />
        <RadioContent>
          <RadioTitle>Unassign — route to Org Admins</RadioTitle>
          <RadioDesc>
            {dotWord(openRedDotCount)} are unassigned; Org Admins are notified and can re-route
            them.
          </RadioDesc>
        </RadioContent>
      </RadioLabel>
    </RadioGroup>
  );
}

export default RedDotDispositionOptions;
